import React from 'react';
import PropTypes from 'prop-types';

/**
 * OnboardingChaptersReady - Screen 3 of chapters onboarding
 *
 * Reveals the generated chapters, creates excitement.
 * Chapter 1 is highlighted as the starting point.
 */
function OnboardingChaptersReady({
  userName,
  birthYear,
  hometown,
  chapters,
  onStartChapter,
}) {
  return (
    <div className="min-h-screen bg-fb-cream py-8 px-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="text-5xl mb-4">📖</div>
          <h1 className="font-serif text-2xl font-bold text-gray-900 mb-2">
            The Story of {userName}
          </h1>
          <p className="font-serif text-base text-gray-600">
            Born {birthYear}
            {hometown && ` in ${hometown}`}
          </p>
        </div>

        {/* Intro text */}
        <p className="font-serif text-lg text-gray-700 text-center mb-6">
          Here are the chapters of your life:
        </p>

        {/* Chapters List */}
        <div className="space-y-3 mb-6">
          {chapters.map((chapter, index) => {
            const isFirst = index === 0;
            const endYearDisplay = chapter.end_year || chapter.endYear || 'Present';

            return (
              <div
                key={chapter.id}
                className={`
                  rounded-xl p-4
                  transition-all duration-200
                  ${isFirst
                    ? 'bg-white border-2 border-fb-terra shadow-md'
                    : 'bg-white border border-gray-200'
                  }
                `}
              >
                <div className="flex items-start gap-4">
                  {/* Chapter number badge */}
                  <div
                    className={`
                      w-10 h-10 rounded-full flex items-center justify-center
                      font-bold text-lg flex-shrink-0
                      ${isFirst
                        ? 'bg-fb-terra text-white'
                        : 'bg-gray-100 text-gray-600'
                      }
                    `}
                  >
                    {chapter.number}
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className={`
                        font-serif font-bold text-lg
                        ${isFirst ? 'text-fb-terra' : 'text-gray-800'}
                      `}>
                        "{chapter.title}"
                      </h3>
                      <span className="font-sans text-sm text-gray-500">
                        {chapter.start_year || chapter.startYear}–{endYearDisplay}
                      </span>
                    </div>

                    {isFirst && (
                      <p className="font-serif text-sm text-fb-terra mt-1 flex items-center gap-1">
                        <span>✨</span>
                        Start here — {chapter.starter_prompt || chapter.starterPrompt}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Reassurance text */}
        <p className="font-serif text-sm text-gray-500 text-center italic mb-8">
          You can rename these, add new ones, or remove any that don't fit.
        </p>

        {/* CTA Button */}
        <button
          onClick={() => onStartChapter(chapters[0]?.id)}
          className="
            w-full bg-fb-terra hover:bg-fb-terra/90 text-white
            font-bold text-lg py-4 px-6
            rounded-xl shadow-md
            transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-fb-terra focus:ring-offset-2
            flex items-center justify-center gap-2
          "
        >
          <span>🎙</span>
          <span>Start Chapter 1 →</span>
        </button>
      </div>
    </div>
  );
}

OnboardingChaptersReady.propTypes = {
  userName: PropTypes.string.isRequired,
  birthYear: PropTypes.number.isRequired,
  hometown: PropTypes.string,
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      number: PropTypes.number.isRequired,
      title: PropTypes.string.isRequired,
      start_year: PropTypes.number,
      startYear: PropTypes.number,
      end_year: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      endYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      starter_prompt: PropTypes.string,
      starterPrompt: PropTypes.string,
    })
  ).isRequired,
  onStartChapter: PropTypes.func.isRequired,
};

OnboardingChaptersReady.defaultProps = {
  hometown: '',
};

export default OnboardingChaptersReady;
